import React from 'react';
import { AutoSizer } from 'react-virtualized';

import * as d3 from 'd3';
import _ from 'lodash';
import { Help, HelpOutline } from '@mui/icons-material';
import { Stack, Tooltip } from '@mui/material';

import { Analytics } from '@/app/analytics/Analytics';
import { AnalyticsEvent } from '@/app/analytics/AnalyticsEvent';
import { OpenCloseMarker } from '@/app/components/OpenCloseMarker';

import { getTimelineMarkerIdsForLogEntry } from './getTimelineMarkerIdsForLogEntry';
import {
  GpsHistoryLocationTimelineMarker,
  isGpsHistoryLocationTimelineMarker,
  isMapEventTimelineMarker,
  MapEventInfo,
  MapEventTimelineMarker,
  TimelineData,
  TimelineMarker,
  TimelineRowGroup,
} from './TimelineData';
import { getTimelineSettings, TimelineSettings } from './TimelineSettings';
import { TimelineTooltip } from './TimelineTooltip';

type Props = {
  data: TimelineData;
  delegate: TimelineDelegate;
  hoveredLogEntry?: {
    logEntryIndex: number;
    timeMs: number;
  };
};

export interface TimelineDelegate {
  onRequestJumpToLogEntry(logEntryIndex: number): void;
  onRequestSelectGpsHistoryLocation(
    gpsHistoryLocationIndex: number,
    color: string | undefined,
  ): void;
  onRequestSelectMapEvent(logEntryIndex: number, mapEvent: MapEventInfo): void;
  convertLogEntryIndexToLineIndex(logEntryIndex: number): number | undefined;
}

type MarkerFrame = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

type MouseWheelEvent = {
  dx: number;
  dy: number;
  time: number;
  metaKey: boolean;
};

type RenderContext = {
  width: number;
  height: number;
  devicePixelRatio: number;

  xScaleOriginal: d3.ScaleTime<number, number, never>;
  xScale: d3.ScaleTime<number, number, never>;
  xAxis: d3.Axis<Date | d3.NumberValue>;
  xAxisGroup: d3.Selection<SVGGElement, unknown, null, undefined>;

  rowHeight: number;
  marginBetweenRows: number;

  margin: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };

  logId: string;
  markerPerId: Map<number, TimelineMarker>;
  lastRenderedMarkerFrames: Map<number, MarkerFrame>;

  highlightedMarkerIds?: Set<number>;

  maxZoom: number;
  transform: d3.ZoomTransform;
  lastMouseWheelEvents: MouseWheelEvent[];
  lastMouseMoveEvent: MouseEvent | undefined;
  selectedTimeMs: number | undefined;
  zoomingToSelectionStartingPosition?: {
    time: number;
    x: number;
  };

  svgViewBar: d3.Selection<SVGRectElement, unknown, null, undefined>;
};

export class Timeline extends React.Component<Props> {
  private svgRef = React.createRef<SVGSVGElement>();
  private canvasRef = React.createRef<HTMLCanvasElement>();
  private tooltipRef = React.createRef<TimelineTooltip>();

  private renderContext?: RenderContext;
  private timer?: d3.Timer;
  private metaKey: boolean = false;
  private metaKeySetTime = 0;
  private leftPanelWidth = 240;
  private marginTop = 24;
  private marginBetweenRows = 2;
  private rowHeight = 20;
  private scrollContainer?: HTMLElement;

  private settings: TimelineSettings = getTimelineSettings();

  constructor(props: Props) {
    super(props);
  }

  componentDidMount() {
    Analytics.log(AnalyticsEvent.TimelineShown);

    window.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.metaKey !== this.metaKey) {
        this.metaKeySetTime = Date.now();
        this.metaKey = event.metaKey;
      }
    });
    window.addEventListener('keyup', (event: KeyboardEvent) => {
      if (event.metaKey !== this.metaKey) {
        this.metaKeySetTime = Date.now();
        this.metaKey = event.metaKey;
      }
    });

    this.timer = d3.timer(() => {
      this.redrawCanvas();
    });
  }

  componentWillUnmount() {
    if (this.timer) {
      this.timer.stop();
      this.timer = undefined;
    }
  }

  render() {
    let totalHeight = this.marginTop;
    this.props.data.rowGroups.forEach((rowGroup) => {
      totalHeight += rowGroup.expanded
        ? (this.rowHeight + this.marginBetweenRows) * rowGroup.rows.length
        : this.rowHeight + this.marginBetweenRows;
    });

    return (
      <AutoSizer
        className="timeline-container"
        style={{
          position: 'relative',
          width: '100%',
          height: `${totalHeight}px`,
          overflowX: 'clip',
        }}
      >
        {(args: { width: number; height: number }) => {
          this.onSizeUpdate({ width: args.width, height: Math.max(args.height, totalHeight) });

          return (
            <>
              <canvas style={{ position: 'absolute' }} ref={this.canvasRef} />
              <svg style={{ position: 'fixed', pointerEvents: 'none' }} ref={this.svgRef} />

              <div
                className="timeline-left-panel"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: this.leftPanelWidth,
                  height: args.height - this.marginTop,
                }}
              >
                {this.leftPanelContent()}
              </div>

              <Tooltip
                className="timeline-help-tooltip"
                disableInteractive
                title={
                  <>
                    <div>Controls:</div>
                    <ul style={{ marginTop: 0 }}>
                      <li>pan: scroll left/right (not in Safari)</li>
                      <li>pan: move pointer while holding shift button</li>

                      <li style={{ marginTop: '8px' }}>vertical scroll: as usual scroll up/down</li>

                      <li style={{ marginTop: '8px' }}>zoom: hold ⌘ and scroll up/down</li>
                      <li>zoom: select range on timeline</li>

                      <li style={{ marginTop: '8px' }}>
                        click on marker jumps to the corresponding log line
                      </li>
                      <li>
                        click on empty space in timeline creates a tick, which can be used for
                        getting the time delta between tick timestamp and timestamp under cursor
                      </li>

                      <li style={{ marginTop: '8px' }}>
                        ⌘ + click on marker jumps to a corresponding alternative log entry
                      </li>
                      <li>
                        for instance, click jumps to network request log entry, ⌘ + click jumps to
                        network response log entry
                      </li>

                      <li style={{ marginTop: '8px' }}>
                        timeline can be resized (drag the thingy on the top of timeline)
                      </li>
                    </ul>
                  </>
                }
              >
                <Help />
              </Tooltip>

              <TimelineTooltip ref={this.tooltipRef} />
            </>
          );
        }}
      </AutoSizer>
    );
  }

  private leftPanelContent(): React.JSX.Element {
    let topOffset = this.marginTop;
    const elements: React.JSX.Element[] = [];

    this.props.data.rowGroups.forEach((rowGroup) => {
      const rowGroupHeight = rowGroup.expanded
        ? (this.rowHeight + this.marginBetweenRows) * rowGroup.rows.length
        : this.rowHeight + this.marginBetweenRows;

      const tooltipContent = this.rowGroupTooltipContent(rowGroup);

      elements.push(
        <div
          style={{
            position: 'absolute',
            top: topOffset,
            height: rowGroupHeight,
            left: 0,
            right: 0,
          }}
          className="timeline-left-panel-row-group"
          key={rowGroup.name}
          onClick={() => {
            rowGroup.expanded = !rowGroup.expanded;
            this.settings.setRowGroupExpanded({
              rowGroupName: rowGroup.name,
              expanded: rowGroup.expanded,
            });
            this.forceUpdate();
          }}
        >
          <div style={{ paddingLeft: 4 }}>
            <Stack direction="row" spacing="0" alignItems="center">
              <OpenCloseMarker open={rowGroup.expanded} />
              &nbsp;
              {rowGroup.name}
              &nbsp;
              {rowGroup.expanded && tooltipContent ? (
                <Tooltip title={tooltipContent}>
                  <HelpOutline className="timeline-row-group-tooltip-icon" />
                </Tooltip>
              ) : null}
            </Stack>
          </div>
        </div>,
      );

      topOffset += rowGroupHeight;
    });

    return <>{elements}</>;
  }

  private rowGroupTooltipContent(rowGroup: TimelineRowGroup) {
    if (rowGroup.name === 'GPS updates') {
      return (
        <div>
          GPS updates are color-coded:
          <ul>
            <li>red is the earliest</li>
            <li>purple is the latest</li>
          </ul>
          Click on the marker shows the location on the map.
        </div>
      );
    }

    if (rowGroup.name === 'Network requests') {
      return (
        <div>
          <ul>
            <li>Click on the marker jumps to the log line with network request.</li>
            <li>⌘ + Click on the marker jumps to the log line with network response.</li>
          </ul>
        </div>
      );
    }

    if (rowGroup.name === 'Analytics') {
      return (
        <div>
          <ul>
            <li>Blue markers are screen events (when screen is shown)</li>
            <li>Khaki markers are generic events (when user taps on something, etc)</li>
          </ul>
        </div>
      );
    }

    if (rowGroup.name === 'Map updates') {
      return (
        <div>
          Color coding:
          <ul>
            <li>
              beige means <span className="monospace">isFromGestureRecognizer</span> was false -
              this map viewport change was done from the code
            </li>
            <li>
              green means <span className="monospace">isFromGestureRecognizer</span> was true - this
              map viewport change was done manually by the user
            </li>
          </ul>
          Interaction:
          <ul>
            <li>click on the marker shows map viewport</li>
            <li>red rectangle is full map viewport</li>
            <li>green rectangle is map viewport with applied paddings</li>
          </ul>
          Structure:
          <ul>
            <li>
              <span className="monospace">mapViewportWillMove</span> indicates that map change is
              starting
            </li>
            <li>
              <span className="monospace">mapViewportUpdate</span> is one of the intermediate
              updates
            </li>
            <li>
              <span className="monospace">mapViewportIdle</span> indicates that map update is
              finished
            </li>
          </ul>
          Sometimes map updates starts from <span className="monospace">mapViewportUpdate</span>.
          For these cases slightly different coloration is used.
        </div>
      );
    }

    if (rowGroup.name === 'Application events') {
      return (
        <div>
          Here listed the events like <span className="monospace">viewDidAppear</span>,
          <span className="monospace">viewDidDisappear</span> and also
          <span className="monospace">pushViewController:animated:</span>.
        </div>
      );
    }

    if (rowGroup.name === 'Router') {
      return <div>Attaching and detaching RIB routers.</div>;
    }

    if (rowGroup.name === 'Application state') {
      return (
        <ul>
          <li>Violet &quot;App is active&quot; marker shows when app was in front of the user.</li>
        </ul>
      );
    }

    return null;
  }

  private onSizeUpdate(args: { width: number; height: number }) {
    if (Math.abs(args.width) < 0.001 || Math.abs(args.height) < 0.001) {
      return;
    }
    if (this.svgRef.current === null || this.canvasRef.current === null) {
      // Not yet rendered.
      return;
    }
    if (this.renderContext) {
      if (
        this.renderContext.width === args.width &&
        this.renderContext.height === args.height &&
        this.renderContext.logId === this.props.data.logId
      ) {
        // Skip because size wasn't changed.
        return;
      }
    }

    const width = args.width;
    const height = args.height;

    const svg = d3.select(this.svgRef.current);
    svg.attr('width', width);
    svg.attr('height', height);

    const canvas = d3.select(this.canvasRef.current);
    const pixelRatio = window.devicePixelRatio || 1;
    canvas.attr('width', width * pixelRatio);
    canvas.attr('height', height * pixelRatio);
    canvas.style('width', `${width}px`);
    canvas.style('height', `${height}px`);

    // canvas.style('left', `${this.leftPanelWidth}px`);
    // svg.style('left', `${this.leftPanelWidth}px`);

    const marginLeft = this.leftPanelWidth;
    const marginRight = 24;
    const marginBottom = 24;
    const data = this.props.data;

    const xScale = d3
      .scaleTime()
      .domain([data.startTimeMs, data.endTimeMs])
      .range([marginLeft, width - marginRight]);

    const xAxis = d3.axisBottom(xScale);

    svg.attr('viewBox', [0, 0, width, height]);
    svg.selectAll('*').remove();

    const context2d = canvas.node()!.getContext('2d')!;
    context2d.font = '14px Arial';
    context2d.fillStyle = '#000000';
    context2d.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

    const viewBar = svg
      .append('rect')
      .attr('x', marginLeft)
      .attr('y', 0)
      .attr('width', width - marginLeft)
      .attr('height', 4)
      .attr('style', 'fill:#000');

    const xAxisGroup = svg.append('g').attr('transform', `translate(0, 4)`).call(xAxis);

    const durationInSecForMaxZoom = 2.0;
    const totalLogDuration = (data.endTimeMs - data.startTimeMs) / 1000.0;
    const maxZoom = Math.max(1, totalLogDuration / durationInSecForMaxZoom);

    canvas.on('mousewheel', (event: WheelEvent) => {
      if (!this.renderContext) {
        return;
      }

      this.handleMouseWheelEvent(event);
    });

    canvas.on('keydown', (event: KeyboardEvent) => {
      if (event.metaKey !== this.metaKey) {
        this.metaKeySetTime = Date.now();
        this.metaKey = event.metaKey;
      }
    });
    canvas.on('keyup', (event: KeyboardEvent) => {
      if (event.metaKey !== this.metaKey) {
        this.metaKeySetTime = Date.now();
        this.metaKey = event.metaKey;
      }
    });

    canvas.on('mouseleave', (event: MouseEvent) => {
      if (!this.renderContext) {
        return;
      }

      this.tooltipRef.current?.setVisible(false);

      if (event.offsetX <= this.renderContext.margin.left) {
        this.handleCanvasMouseUpEvent(event);
      }
      this.renderContext.zoomingToSelectionStartingPosition = undefined;
    });

    canvas.on('mousedown', (event: MouseEvent) => {
      if (!this.renderContext) {
        return;
      }
      this.renderContext.zoomingToSelectionStartingPosition = {
        x: event.offsetX,
        time: this.renderContext.xScale.invert(event.offsetX).getTime(),
      };
    });

    canvas.on('mouseup', (event: MouseEvent) => {
      this.handleCanvasMouseUpEvent(event);
    });

    canvas.on('mousemove', (event: MouseEvent) => {
      if (!this.renderContext) {
        return;
      }
      this.renderContext.lastMouseMoveEvent = event;
      const newHighlightedMarkerIds = this.findRenderedMarkerIdsForXY(event.offsetX, event.offsetY);

      if (event.shiftKey) {
        const newX = this.clampPan(this.renderContext.transform.x + event.movementX);
        this.updateTransform({ x: newX });
      }

      const hasAtLeastOneMarker = newHighlightedMarkerIds && newHighlightedMarkerIds.size > 0;
      // svg.style('cursor', hasAtLeastOneMarker ? 'pointer' : 'inherit');

      if (hasAtLeastOneMarker) {
        this.tooltipRef.current?.setVisible(true);

        const tooltipX =
          event.offsetX > this.renderContext.width * 0.5
            ? event.offsetX - 8 - this.renderContext.width
            : event.offsetX + 16;
        const tooltipY =
          event.offsetY > this.renderContext.height * 0.5
            ? event.offsetY - 8 - this.renderContext.height
            : event.offsetY + 16;
        this.tooltipRef.current?.setPosition(tooltipX, tooltipY);

        const markers = Array.from(newHighlightedMarkerIds.keys()).map(
          (markerId) => this.renderContext!.markerPerId.get(markerId)!,
        );
        this.tooltipRef.current?.setMarkers(markers);
      } else {
        this.tooltipRef.current?.setVisible(false);
      }

      if (!_.isEqual(this.renderContext.highlightedMarkerIds, newHighlightedMarkerIds)) {
        this.renderContext.highlightedMarkerIds = newHighlightedMarkerIds;
      }
    });
    canvas.on('click', (event: MouseEvent) => {
      if (!this.renderContext) {
        return;
      }
      const newHighlightedMarkerIds = this.findRenderedMarkerIdsForXY(event.offsetX, event.offsetY);
      if (!newHighlightedMarkerIds || newHighlightedMarkerIds?.size === 0) {
        this.renderContext.selectedTimeMs = this.renderContext.xScale
          .invert(event.offsetX)
          .getTime();
        return;
      }

      const sortedMarkerIds = Array.from(newHighlightedMarkerIds.keys()).sort();
      // Take the last marker because it is the topmost marker in the timeline UI.
      const markerId = sortedMarkerIds[sortedMarkerIds.length - 1];
      const marker = this.renderContext.markerPerId.get(markerId);
      if (!marker) {
        return;
      }

      Analytics.log(AnalyticsEvent.TimelineClickOnMarker);

      let logEntryIndexToJumpTo = marker.logEntry.index;

      if (event.metaKey && marker.alternativeLogEntry) {
        logEntryIndexToJumpTo = marker.alternativeLogEntry.index;
      }

      this.renderContext.selectedTimeMs = marker.startTimeMs;

      if (isGpsHistoryLocationTimelineMarker(marker)) {
        const gpsMarker = marker as GpsHistoryLocationTimelineMarker;
        this.props.delegate.onRequestSelectGpsHistoryLocation(
          gpsMarker.gpsHistoryLocationIndex,
          gpsMarker.fillColor,
        );
      } else if (isMapEventTimelineMarker(marker)) {
        const mapEventMarker = marker as MapEventTimelineMarker;
        this.props.delegate.onRequestSelectMapEvent(logEntryIndexToJumpTo, mapEventMarker);
      } else {
        this.props.delegate.onRequestJumpToLogEntry(logEntryIndexToJumpTo);
      }
    });

    const markerPerId = new Map<number, TimelineMarker>();
    for (const group of this.props.data.rowGroups) {
      for (const row of group.rows) {
        for (const marker of row.markers) {
          if (markerPerId.has(marker.markerId)) {
            console.error('Duplicate marker ID', marker.markerId);
            continue;
          }
          markerPerId.set(marker.markerId, marker);
        }
      }
    }

    this.renderContext = {
      width,
      height,
      xScaleOriginal: xScale,
      devicePixelRatio: window.devicePixelRatio,
      xScale,
      xAxis,
      xAxisGroup,
      rowHeight: this.rowHeight,
      marginBetweenRows: this.marginBetweenRows,
      margin: {
        top: this.marginTop,
        bottom: marginBottom,
        left: marginLeft,
        right: marginRight,
      },
      logId: data.logId,
      markerPerId,
      lastRenderedMarkerFrames: new Map(),

      maxZoom,
      transform: new d3.ZoomTransform(1, 0, 0),
      lastMouseWheelEvents: [],
      lastMouseMoveEvent: undefined,
      selectedTimeMs: undefined,

      svgViewBar: viewBar,
    };

    this.updateXAxisGroup();
  }

  private handleCanvasMouseUpEvent(event: MouseEvent) {
    if (!this.renderContext) {
      return;
    }

    const minPixelsToDragToDoSelection = 4;
    const data = this.props.data;

    if (typeof this.renderContext.zoomingToSelectionStartingPosition !== 'undefined') {
      const pixelsMoved = Math.abs(
        this.renderContext.zoomingToSelectionStartingPosition.x - event.offsetX,
      );
      const startingPos = this.renderContext.zoomingToSelectionStartingPosition;
      this.renderContext.zoomingToSelectionStartingPosition = undefined;

      if (pixelsMoved < minPixelsToDragToDoSelection) {
        return;
      }

      const lastTime = this.renderContext.xScale.invert(event.offsetX).getTime();

      let t0 = Math.min(lastTime, startingPos.time);
      t0 = _.clamp(t0, data.startTimeMs, data.endTimeMs);

      let t1 = Math.max(lastTime, startingPos.time);
      t1 = _.clamp(t1, data.startTimeMs, data.endTimeMs);

      if (Math.abs(t1 - t0) > 0.01) {
        const targetScale = this.clampZoom((data.endTimeMs - data.startTimeMs) / (t1 - t0));
        const widthWithoutMargin =
          this.renderContext.width -
          this.renderContext.margin.left -
          this.renderContext.margin.right;

        const offsetT0Factor = (t0 - data.startTimeMs) / (data.endTimeMs - data.startTimeMs);
        const targetT0Offset = this.clampPan(
          -(this.renderContext.margin.left * targetScale - this.renderContext.margin.left) -
            offsetT0Factor * (widthWithoutMargin * targetScale),
          targetScale,
        );

        const offsetT1Factor = (t1 - data.startTimeMs) / (data.endTimeMs - data.startTimeMs);
        const targetT1Offset = this.clampPan(
          -(this.renderContext.margin.left * targetScale - this.renderContext.margin.left) -
            offsetT1Factor * (widthWithoutMargin * targetScale),
          targetScale,
        );

        let targetOffset = targetT0Offset;
        const widthBetweenT1AndT0 = Math.abs(targetT1Offset - targetT0Offset);
        if (widthBetweenT1AndT0 < widthWithoutMargin) {
          targetOffset += (widthWithoutMargin - widthBetweenT1AndT0) * 0.5;
        }

        this.updateTransform({ x: targetOffset, k: targetScale });
      }
    }
  }

  private redrawCanvas() {
    if (!this.renderContext) {
      // Not yet rendered.
      return;
    }

    if (!this.scrollContainer) {
      const scrollContainer = this.canvasRef?.current?.parentElement?.parentElement;
      if (scrollContainer) {
        this.scrollContainer = scrollContainer;
      }
    }
    const scrollTop = this.scrollContainer?.scrollTop || 0;

    const renderContext = this.renderContext;

    const canvas = this.canvasRef.current!;
    const context2d = canvas.getContext('2d')!;
    context2d.clearRect(0, 0, canvas.width, canvas.height);

    //
    context2d.strokeStyle = '#aaaaaa';
    context2d.lineWidth = 1;
    context2d.beginPath();
    context2d.moveTo(0, 0);
    context2d.lineTo(canvas.width, 0);
    context2d.stroke();

    const date0 = renderContext.xScale.invert(renderContext.margin.left).getTime();

    const widthWithoutPixelRatio = canvas.width / window.devicePixelRatio;
    const date1 = renderContext.xScale
      .invert(widthWithoutPixelRatio - renderContext.margin.right)
      .getTime();

    const totalTime = this.props.data.endTimeMs - this.props.data.startTimeMs;
    const visiblePart = (date1 - date0) / totalTime;
    const offsetFactorBefore = (date0 - this.props.data.startTimeMs) / totalTime;
    const fullWidthForOverviewBar =
      widthWithoutPixelRatio - renderContext.margin.left - renderContext.margin.right;
    renderContext.svgViewBar
      .attr('x', renderContext.margin.left + offsetFactorBefore * fullWidthForOverviewBar)
      .attr('width', Math.max(20, visiblePart * fullWidthForOverviewBar));

    //
    renderContext.lastRenderedMarkerFrames.clear();
    let nextRowIndex = 0;

    for (const group of this.props.data.rowGroups) {
      let minY = 0;
      let maxY = 0;

      for (
        let rowIndexInThisGroup = 0;
        rowIndexInThisGroup < group.rows.length;
        ++rowIndexInThisGroup
      ) {
        const row = group.rows[rowIndexInThisGroup];

        const rowYOffset =
          renderContext.margin.top +
          nextRowIndex * (renderContext.rowHeight + renderContext.marginBetweenRows);

        if (rowIndexInThisGroup === 0) {
          minY = rowYOffset;
        }

        maxY =
          renderContext.margin.top +
          (nextRowIndex + 1) * (renderContext.rowHeight + renderContext.marginBetweenRows);

        for (
          let markerIndexInThisRow = 0;
          markerIndexInThisRow < row.markers.length;
          ++markerIndexInThisRow
        ) {
          const marker = row.markers[markerIndexInThisRow];

          let x1 = renderContext.xScale(marker.startTimeMs);
          const minWidthInPixels = 16;
          let x2 = marker.endTimeMs
            ? renderContext.xScale(marker.endTimeMs)
            : x1 + minWidthInPixels;

          const y1 = rowYOffset;

          if (x1 < renderContext.margin.left && x2 < renderContext.margin.left) {
            continue;
          }
          const startsBeforeLeftBorder = x1 < renderContext.margin.left;
          x1 = Math.max(x1, renderContext.margin.left);
          x2 = Math.max(x2, renderContext.margin.left);

          const w = Math.max(5, x2 - x1);
          x2 = x1 + w;
          const h = renderContext.rowHeight;
          const y2 = y1 + h;

          context2d.fillStyle = marker.fillColor;
          context2d.fillRect(x1, y1, w, h);

          context2d.strokeStyle = '#676';
          context2d.lineWidth = 1;
          if (startsBeforeLeftBorder) {
            context2d.beginPath();
            context2d.moveTo(x1, y1);
            context2d.lineTo(x2, y1);
            context2d.lineTo(x2, y2);
            context2d.lineTo(x1, y2);
            context2d.stroke();
          } else {
            context2d.strokeRect(x1, y1, w, h);
          }

          if (marker.textOnMarker && group.expanded) {
            let maxWidth = 500;
            const nextMarkerIndex = markerIndexInThisRow + 1;
            if (nextMarkerIndex < row.markers.length) {
              const nextMarker = row.markers[nextMarkerIndex];
              const nextMarkerX1 = renderContext.xScale(nextMarker.startTimeMs);
              maxWidth = nextMarkerX1 - x1 - 2;
            }

            const widthPerCharacter = 6;

            if (maxWidth > widthPerCharacter) {
              let charactersInMaxWidth = Math.round(maxWidth / widthPerCharacter);
              charactersInMaxWidth = Math.min(charactersInMaxWidth, marker.textOnMarker.length);

              const textToDisplay = marker.textOnMarker.substring(0, charactersInMaxWidth);

              context2d.font = '12px Arial';
              context2d.fillStyle = marker.textOnMarkerColor || '#000000';
              context2d.fillText(textToDisplay, x1 + 0, y1 + 14, maxWidth);
            }
          }

          const markerFrame: MarkerFrame = {
            x1,
            y1,
            x2,
            y2,
          };
          renderContext.lastRenderedMarkerFrames.set(marker.markerId, markerFrame);
        }

        if (group.expanded) {
          nextRowIndex += 1;
        }
      }

      if (!group.expanded) {
        nextRowIndex += 1;
      }

      context2d.strokeStyle = '#ddd';
      context2d.lineWidth = 1;
      context2d.beginPath();
      context2d.moveTo(renderContext.width, maxY - renderContext.marginBetweenRows * 0.5);
      context2d.lineTo(0, maxY - renderContext.marginBetweenRows * 0.5);
      context2d.stroke();
    }

    if (renderContext.highlightedMarkerIds) {
      Array.from(renderContext.highlightedMarkerIds).forEach((markerId) => {
        this.drawMarkerOutline(context2d, markerId, this.metaKey ? 2 : 1);
      });
    }

    if (this.props.hoveredLogEntry) {
      const hoveredMarkerIds = getTimelineMarkerIdsForLogEntry(
        renderContext.markerPerId.values(),
        this.props.hoveredLogEntry.logEntryIndex,
      );

      Array.from(hoveredMarkerIds).forEach((markerId) => {
        this.drawMarkerOutline(context2d, markerId, 2);
      });
    }

    context2d.clearRect(0, 0, renderContext.width, renderContext.margin.top + scrollTop - 1);

    if (this.props.hoveredLogEntry) {
      const x = renderContext.xScale(this.props.hoveredLogEntry.timeMs);
      const isInsideTimeline =
        x >= renderContext.margin.left && x <= renderContext.width - renderContext.margin.right;

      if (isInsideTimeline) {
        context2d.save();
        context2d.strokeStyle = '#ff9800';
        context2d.lineWidth = 2;
        context2d.setLineDash([5, 4]);
        context2d.beginPath();
        context2d.moveTo(x, 0);
        context2d.lineTo(x, renderContext.height);
        context2d.stroke();
        context2d.restore();
      }
    }

    if (typeof renderContext.selectedTimeMs !== 'undefined') {
      const x = renderContext.xScale(renderContext.selectedTimeMs);

      context2d.strokeStyle = '#bbb';
      context2d.lineWidth = 1;
      context2d.beginPath();
      context2d.moveTo(x, 0);
      context2d.lineTo(x, renderContext.height);
      context2d.stroke();

      const str = this.getDateStr(new Date(renderContext.selectedTimeMs));

      context2d.font = '10px Arial';
      const textMeasurements = context2d.measureText(str);

      context2d.fillStyle = '#fefefeaa';
      context2d.fillRect(
        x + 2,
        scrollTop + renderContext.margin.top + 8 - 10,
        textMeasurements.width,
        12,
      );

      context2d.fillStyle = '#555';
      context2d.fillText(
        str,
        x + 2,
        scrollTop + renderContext.margin.top + 8,
        textMeasurements.width,
      );
    }

    if (renderContext.lastMouseMoveEvent) {
      const x = renderContext.lastMouseMoveEvent.offsetX;

      context2d.strokeStyle = '#888';
      context2d.lineWidth = 1;
      context2d.beginPath();
      context2d.moveTo(x, 0);
      context2d.lineTo(x, renderContext.height);
      context2d.stroke();

      const dateUnderCursor = renderContext.xScale.invert(x);
      let str = this.getDateStr(dateUnderCursor);

      if (typeof renderContext.selectedTimeMs !== 'undefined') {
        const delta = dateUnderCursor.getTime() - renderContext.selectedTimeMs;
        str += ` (𝚫 ${this.getDeltaStr(delta)})`;
      }

      context2d.font = '10px Arial';

      const textMeasurements = context2d.measureText(str);

      context2d.fillStyle = '#fefefeaa';
      context2d.fillRect(
        x + 2,
        scrollTop + renderContext.margin.top - 12 - 10,
        textMeasurements.width,
        10,
      );

      context2d.fillStyle = '#000000';
      context2d.fillText(
        str,
        x + 2,
        scrollTop + renderContext.margin.top - 12,
        textMeasurements.width,
      );

      if (typeof this.renderContext.zoomingToSelectionStartingPosition !== 'undefined') {
        const initialSelectionX = renderContext.xScale(
          this.renderContext.zoomingToSelectionStartingPosition.time,
        );

        const x0 = Math.min(initialSelectionX, x);
        const x1 = Math.max(initialSelectionX, x);

        context2d.fillStyle = 'rgba(0,0,0,0.1)';
        context2d.fillRect(x0, 0, x1 - x0, renderContext.height);
      }
    }
  }

  private drawMarkerOutline(
    context2d: CanvasRenderingContext2D,
    markerId: number,
    lineWidth: number,
  ) {
    const frame = this.renderContext?.lastRenderedMarkerFrames.get(markerId);
    if (!frame) {
      return;
    }

    context2d.strokeStyle = '#000';
    context2d.lineWidth = lineWidth;
    context2d.strokeRect(frame.x1, frame.y1, frame.x2 - frame.x1, frame.y2 - frame.y1);

    context2d.strokeStyle = '#656565';
    context2d.lineWidth = lineWidth;
    context2d.strokeRect(
      frame.x1 + 1,
      frame.y1 + 1,
      frame.x2 - frame.x1 - 2,
      frame.y2 - frame.y1 - 2,
    );
  }

  private getDateStr(date: Date): string {
    const h = _.padStart(date.getHours().toString(), 2, '0');
    const m = _.padStart(date.getMinutes().toString(), 2, '0');
    const s = _.padStart(date.getSeconds().toString(), 2, '0');
    const ms = _.padStart(date.getMilliseconds().toString(), 3, '0');
    const timeStr = `${h}:${m}:${s}.${ms}`;
    return timeStr;
  }

  private getDeltaStr(deltaMs: number): string {
    const sec = deltaMs / 1000.0;
    const h = Math.floor(sec / (60 * 60));
    const m = Math.floor((sec - h * 60 * 60) / 60);
    const s0 = sec - h * 60 * 60 - m * 60;
    const s = Math.floor(s0);
    const ms = Math.round((s0 - s) * 1000.0);

    let str = '';
    if (h > 0) {
      str += h.toString();
      str += 'h';
    }

    if (m > 0) {
      if (str.length > 0) {
        str += ' ';
      }

      str += m.toString();
      str += 'm';
    }

    if (s > 0) {
      if (str.length > 0) {
        str += ' ';
      }

      str += s.toString();
      str += 's';
    }

    if (ms > 0) {
      if (str.length > 0) {
        str += ' ';
      }

      str += _.padStart(ms.toString(), 3, '0');
      str += 'ms';
    }

    return str;
  }

  private findRenderedMarkerIdsForXY(x: number, y: number): Set<number> | undefined {
    if (!this.renderContext) {
      // Not yet rendered.
      return;
    }

    const ids = new Set<number>();

    for (const entry of Array.from(this.renderContext.lastRenderedMarkerFrames.entries())) {
      const frame = entry[1];

      const isInsideFrame = x >= frame.x1 && x <= frame.x2 && y >= frame.y1 && y <= frame.y2;
      if (isInsideFrame) {
        ids.add(entry[0]);
      }
    }

    return ids;
  }

  private handleMouseWheelEvent(event: WheelEvent) {
    if (!this.renderContext) {
      // Not yet rendered.
      return;
    }

    let isZooming = false;
    let isPanning = false;
    if (event.metaKey || event.shiftKey) {
      isZooming = true;
      event.preventDefault();
    } else {
      isPanning = true;
    }

    if (!isZooming && !isPanning) {
      return;
    }

    if (isPanning) {
      const container = this.canvasRef.current?.parentElement?.parentElement;
      if (container) {
        container.scrollTop += event.deltaY;
      }
    }

    const dk =
      Math.sign(event.deltaY) *
      Math.sqrt(Math.abs(event.deltaY) * 0.01 * this.renderContext.transform.k ** 2) *
      0.2;
    const newKUnclamped = this.renderContext.transform.k - dk;
    const newK = isZooming ? this.clampZoom(newKUnclamped) : this.renderContext.transform.k;

    const prevFullContentWidth = this.renderContext.width * this.renderContext.transform.k;

    const viewportOffset01 = event.clientX / this.renderContext.width;
    const contentOffset01 =
      (-this.renderContext.transform.x + event.clientX) / prevFullContentWidth;

    const newFullContentWidth = this.renderContext.width * newK;
    const newXUnclamped = isPanning
      ? this.renderContext.transform.x - event.deltaX
      : -(contentOffset01 * newFullContentWidth - viewportOffset01 * this.renderContext.width);
    const newX = this.clampPan(newXUnclamped, newK);

    this.updateTransform({ k: newK, x: newX });
  }

  private clampZoom(zoom: number): number {
    const rc = this.renderContext!;
    return _.clamp(zoom, 1.0, rc.maxZoom);
  }

  private clampPan(x: number, k?: number): number {
    const rc = this.renderContext!;
    const scale = typeof k !== 'undefined' ? k : rc.transform.k;
    const widthWithoutMargin = rc.width - rc.margin.left - rc.margin.right;
    const fullContentWidth = widthWithoutMargin * scale;
    const result = _.clamp(
      x,
      -(rc.margin.left * scale - rc.margin.left) - (fullContentWidth - widthWithoutMargin),
      -(rc.margin.left * scale - rc.margin.left),
    );
    return result;
  }

  private updateTransform(args: { k?: number; x?: number }) {
    const rc = this.renderContext!;

    rc.transform = new d3.ZoomTransform(
      typeof args.k === 'undefined' ? rc.transform.k : args.k,
      typeof args.x === 'undefined' ? rc.transform.x : args.x,
      rc.transform.y,
    );
    const newXScale: d3.ScaleTime<number, number, never> = rc.transform.rescaleX(rc.xScaleOriginal);

    rc.xScale = newXScale;
    rc.xAxis.scale(newXScale);
    this.updateXAxisGroup();
  }

  private updateXAxisGroup() {
    const rc = this.renderContext!;

    const timeOnLeftMSec = rc.xScale.invert(0).getTime();
    const timeOnRightMSec = rc.xScale.invert(rc.width).getTime();
    const timeVisibleSec = (timeOnRightMSec - timeOnLeftMSec) / 1000.0;

    const steps: number[] = [
      0.1, 0.25, 0.5, 1, 2, 5, 10, 30, 60, 120, 240, 480, 960, 1200, 2400, 4800, 9600,
    ];

    for (const step of steps) {
      const ticksCount = timeVisibleSec / step;
      const pixelsPerTick = rc.width / ticksCount;
      if (pixelsPerTick < 100) {
        continue;
      }

      let timeInterval: d3.TimeInterval | null;
      let format: string;

      if (step < 1.0) {
        format = '%H:%M:%S.%L';
        timeInterval = d3.timeMillisecond.every(step * 1000.0);
      } else if (step < 60.0) {
        format = '%H:%M:%S';
        timeInterval = d3.timeSecond.every(step);
      } else {
        format = '%H:%M';
        timeInterval = d3.timeMinute.every(step / 60.0);
      }

      rc.xAxis.ticks(timeInterval);
      rc.xAxis.tickFormat((date) => d3.timeFormat(format)(date as Date));

      break;
    }

    rc.xAxisGroup.call(rc.xAxis);
  }
}
